import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { format } from 'date-fns';
import { CreditCard, Loader2, AlertTriangle } from 'lucide-react';
import { captureError } from '@/lib/sentry';
import { useEnterpriseCapabilities } from '@/hooks/useEnterpriseCapabilities';
import { useSeatUsage, usePartnerWorkspaceBreakdown } from '@/hooks/useEnterpriseSeatUsage';
import { SeatUsageStat } from '@/components/enterprise/SeatUsageStat';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/**
 * /enterprise/billing — #610 Phase 2: read-only consolidated seat-usage view.
 *
 * Partner-admin only: the layout tab is `isAnyAdmin`-gated, this page re-gates on
 * `adminPartners` (defense in depth), and the underlying RPCs re-authorize
 * server-side. Shows the deduped seat headline (SeatUsageStat) plus a per-workspace
 * "Active members" breakdown. NO payment / charging / invoices — later phases of #610.
 */
export function EnterpriseBilling(): JSX.Element {
  const { partners, isLoading: capsLoading } = useEnterpriseCapabilities();

  // Billing is admin-only seat data → filter to admin partners. Do NOT use the
  // unfiltered `partners` list the Members page uses: that intentionally includes
  // delegated `emulate`-capability holders, who must not see billing.
  const adminPartners = useMemo(() => partners.filter((p) => p.is_admin), [partners]);

  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const partnerId = selectedPartnerId ?? adminPartners[0]?.partner_id ?? '';

  const { data: rows = [], isLoading: rowsLoading, error, refetch } =
    usePartnerWorkspaceBreakdown(partnerId);

  // Deduped partner-wide headline value, for the reconciliation note. React Query
  // dedupes this call with SeatUsageStat's own useSeatUsage (same partnerId key).
  const seatUsage = useSeatUsage(partnerId);
  const seatsUsed = seatUsage.data?.seats_used;

  // Surface breakdown failures to GlitchTip (CLAUDE.md telemetry convention).
  useEffect(() => {
    if (error) captureError(error as Error, { feature: 'enterprise-billing' });
  }, [error]);

  // --- Gating (after hooks, to satisfy rules-of-hooks) ---------------------
  if (capsLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (adminPartners.length === 0) {
    return <Navigate to="/" replace />;
  }

  const rowsSum = rows.reduce((acc, r) => acc + r.seats, 0);
  const showInlineReconcile = seatsUsed !== undefined && rowsSum > seatsUsed;

  return (
    <div className="container mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-foreground" />
            Billing
          </h1>
          <p className="text-sm text-muted-foreground">
            Seat usage across your enterprise&rsquo;s workspaces.
          </p>
        </div>

        {adminPartners.length > 1 && (
          <div className="w-56 shrink-0">
            <Select value={partnerId} onValueChange={(v) => setSelectedPartnerId(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select enterprise" />
              </SelectTrigger>
              <SelectContent>
                {adminPartners.map((p) => (
                  <SelectItem key={p.partner_id} value={p.partner_id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <SeatUsageStat partnerId={partnerId} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Seats by workspace</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              <span>Couldn&rsquo;t load the workspace breakdown.</span>
              <Button size="sm" variant="outline" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          ) : rowsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No workspaces have been provisioned for this enterprise yet.
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workspace</TableHead>
                    <TableHead className="text-right">Active members</TableHead>
                    <TableHead className="text-right">Workspace created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.workspace_id}>
                      <TableCell className="font-medium">{row.workspace_name}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.seats}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {row.created_at ? format(new Date(row.created_at), 'MMM d, yyyy') : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="mt-3 text-xs text-muted-foreground">
                Members in multiple workspaces count once toward your seat total.
                {showInlineReconcile && ` ${seatsUsed} ${seatsUsed === 1 ? 'seat' : 'seats'} billed.`}
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
